/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { $generateNodesFromDOM } from '@lexical/html';
import { $isLinkNode } from '@lexical/link';
import { $insertGeneratedNodes } from '@lexical/clipboard';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $insertNodeToNearestRoot,
  $wrapNodeInElement,
  isMimeType,
  mediaFileReader,
  mergeRegister,
  objectKlassEquals,
} from '@lexical/utils';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import {
  $getSelection,
  $createParagraphNode,
  $getNodeByKey,
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  ElementNode,
  LexicalNode,
  PASTE_COMMAND,
  $isRootOrShadowRoot,
  PasteCommandType,
  $createTextNode,
  $createTabNode,
} from 'lexical';
import { useEffect } from 'react';
import {
  DocumentRelatedDataStatus,
  DocumentRelatedDataType,
  RecInput,
} from '../../../../../__generated__/graphql';
import {
  startsWithEmoji,
  getEmojisAsArrayFromString,
  removeEmojisFromString,
} from '../../../../../emoji';
import { exportNodeToJSON } from '../../../../../lexical';
import { $createImageNode, $isImageNode } from '../nodes/LexicalImageNode';
import {
  $createRecommendationNode,
  $isRecommendationNode,
} from '../nodes/LexicalRecommendationNode';
import { useRecDraftMutations } from './ComponentPickerPlugin/useComponentPickerHelpers';
import useDocumentHooks from '../../../../hooks/useDocumentHooks';
import { useDocumentWrapperContext } from '../../../../providers/DocumentWrapperContext';
import useFileUpload from '../../../../hooks/useFileUpload';
import { useRecommendationContext } from '../../../by-type/recommendations/providers/useRecommendationContext';

export const ACCEPTABLE_IMAGE_TYPES = [
  'image/',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/webp',
];

export function PastePlugin({
  autoGenerateRecs,
  uploadImageOnRichTextPaste,
  uploadImageOnFilePaste,
}: {
  autoGenerateRecs?: boolean;
  uploadImageOnRichTextPaste?: boolean;
  uploadImageOnFilePaste?: boolean;
}): null {
  const [editor] = useLexicalComposerContext();

  const { createRecDraftNode, createRecRelatedDataFromRecId } = useRecDraftMutations();

  const { document } = useDocumentWrapperContext();

  const { rec } = useRecommendationContext();

  const { createDocumentRelatedData } = useDocumentHooks();

  const {
    uploadFileFromBlob,
    uploadFileFromLink,
    nodes: inputNodes,
  } = useFileUpload({
    onFileSign: async (file) => {
      if (document && !rec) {
        createDocumentRelatedData({
          variables: {
            documentId: document.id,
            input: {
              targetFileId: file.id,
              type: DocumentRelatedDataType.Image,
              status: DocumentRelatedDataStatus.DocumentContent,
            },
          },
        });
      }
    },
  });

  useEffect(() => {
    if (!editor) return;

    return mergeRegister(
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const selection = $getSelection();

          const [, files, hasTextContent] = eventFiles(event);

          if (selection === null) {
            return false;
          }

          if (files.length > 0 && !hasTextContent && uploadImageOnFilePaste) {
            editor.dispatchCommand(DRAG_DROP_PASTE, files);
            return true;
          }

          const dataTransfer =
            event instanceof InputEvent || event instanceof KeyboardEvent
              ? null
              : event.clipboardData;

          if (dataTransfer) {
            const htmlString = dataTransfer.getData('text/html');

            const text =
              dataTransfer.getData('text/plain') || dataTransfer.getData('text/uri-list');

            if (htmlString || text) {
              try {
                let nodes: LexicalNode[] = [];

                if (htmlString) {
                  const parser = new DOMParser();
                  const dom = parser.parseFromString(htmlString, 'text/html');
                  nodes = $generateNodesFromDOM(editor, dom);
                } else {
                  const parts = text.split(/(\r?\n|\t)/);
                  if (parts[parts.length - 1] === '') {
                    parts.pop();
                  }
                  for (let i = 0; i < parts.length; i++) {
                    const part = parts[i];
                    if (part === '\n' || part === '\r\n') {
                      nodes.push($createParagraphNode());
                    } else if (part === '\t') {
                      nodes.push($createTabNode());
                    } else {
                      const paragraph = $createParagraphNode();
                      const node = $createTextNode(part);
                      paragraph.append(node);
                      nodes.push(paragraph);
                    }
                  }
                }

                const hasNodesThatStartWithEmoji = nodes.find((node) => {
                  if ($isRecommendationNode(node)) return false;
                  const str = node.getTextContent();
                  return str ? startsWithEmoji(str) : false;
                });

                const recLinkNodes = nodes.filter((node) => {
                  if ($isRecommendationNode(node)) return false;
                  const recId = extractIdFromUrl(node.getTextContent());
                  return !!recId;
                });

                const newImageNodes = nodes.filter($imageOrImageChild);

                if (
                  (autoGenerateRecs && (hasNodesThatStartWithEmoji || recLinkNodes.length)) ||
                  (uploadImageOnRichTextPaste && newImageNodes.length)
                ) {
                  if (hasNodesThatStartWithEmoji || recLinkNodes.length) {
                    const confirm = window.confirm(
                      'This seems rec-like, do you want to insert these as recs?',
                    );

                    if (!confirm) {
                      return false;
                    }
                  }

                  let nodesToInsert: LexicalNode[] = [];
                  let recsToInsert: (RecInput & {
                    replaceNodeKey: string;
                    deleteNodeKey: string;
                  })[] = [];

                  let imagesToUpload: {
                    nodeKey: string;
                    replaceNodeKey?: string;
                    url: string;
                    link?: string;
                  }[] = [];

                  for (let i = 0; i < nodes.length; i++) {
                    const node = nodes[i];
                    let nextNode = nodes[i + 1];
                    let text = node.getTextContent();
                    let nextText = nextNode?.getTextContent();

                    if (autoGenerateRecs) {
                      const url = $isElementNode(node)
                        ? node.getChildren().find($isLinkNode)?.__url
                        : null;

                      const emojis = getEmojisAsArrayFromString(text);

                      if (emojis.length && nextNode) {
                        const emoji = emojis.join('');
                        const title = removeEmojisFromString(text);
                        const content = nextText;

                        recsToInsert.push({
                          replaceNodeKey: node.getKey(),
                          deleteNodeKey: nextNode.getKey(),
                          emoji,
                          title,
                          content,
                          url,
                          contentLexical: JSON.stringify({
                            root: {
                              direction: 'ltr',
                              format: '',
                              indent: 0,
                              type: 'root',
                              version: 1,
                              children:
                                nextNode.getType() === 'quote'
                                  ? (nextNode as ElementNode).getChildren().map(exportNodeToJSON)
                                  : [exportNodeToJSON(nextNode)],
                            },
                          }),
                        });
                        i++;
                        nodesToInsert.push(node, nextNode);
                        continue;
                      }
                    }

                    if ($imageOrImageChild(node)) {
                      if (!uploadImageOnRichTextPaste) continue;
                      if ($isImageNode(node)) {
                        imagesToUpload.push({ nodeKey: node.getKey(), url: node.__image.url! });
                      } else if ($isLinkNode(node)) {
                        const imageNode = node.getChildren().find($isImageNode);
                        if (imageNode) {
                          imagesToUpload.push({
                            nodeKey: imageNode.getKey(),
                            url: imageNode.__image.url!,
                            replaceNodeKey: node.getKey(),
                            // link: node.__url !== imageNode.__image.url ? node.__url : undefined,
                          });
                        }
                      }
                    }

                    nodesToInsert.push(node);
                    nodesToInsert.push($createParagraphNode());
                  }

                  nodesToInsert.push($createParagraphNode());

                  $insertGeneratedNodes(editor, nodesToInsert, selection);

                  (async () => {
                    for (const recLinkNode of recLinkNodes) {
                      const recId = extractIdFromUrl(recLinkNode.getTextContent());
                      const rec = await createRecRelatedDataFromRecId(recId!);

                      let recNode;
                      editor.update(() => {
                        recNode = $createRecommendationNode(rec!.id, rec!);
                        const nodeToReplace = $getNodeByKey(recLinkNode.__key);
                        try {
                          nodeToReplace?.replace(recNode);
                        } catch (e) {}
                        recNode.selectEnd();
                      });
                    }

                    for (const { replaceNodeKey, deleteNodeKey, ...rest } of recsToInsert) {
                      const rec = await createRecDraftNode(editor, rest);
                      let recNode;
                      editor.update(() => {
                        recNode = $createRecommendationNode(rec!.id, rec!);
                        const nodeToReplace = $getNodeByKey(replaceNodeKey);
                        try {
                          nodeToReplace?.replace(recNode);
                        } catch (e) {}
                        const nodeToDelete = $getNodeByKey(deleteNodeKey);
                        nodeToDelete?.remove();
                        recNode.selectEnd();
                      });
                    }

                    for (const imageToUpload of imagesToUpload) {
                      if (!imageToUpload.url) continue;

                      const file = await uploadFileFromLink(imageToUpload.url);

                      let imageNode;
                      editor.update(() => {
                        imageNode = $getNodeByKey(imageToUpload.nodeKey);
                        imageNode.setImage(file);
                        const nodeToReplace = $getNodeByKey(imageToUpload.replaceNodeKey!);
                        nodeToReplace?.replace(imageNode);
                        imageNode.selectEnd();
                      });
                    }
                  })();

                  console.log({
                    nodesToInsert,
                    recsToInsert,
                    imagesToUpload,
                  });

                  return true;
                } else {
                  if (!uploadImageOnRichTextPaste) {
                    let filteredNodes = !uploadImageOnRichTextPaste
                      ? nodes.filter((node) => !$imageOrImageChild(node))
                      : nodes;
                    $insertGeneratedNodes(editor, filteredNodes, selection);
                    return true;
                  }
                }
              } catch (e) {
                console.log(e);
                // Fail silently.
              }
            }
          }

          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        DRAG_DROP_PASTE,
        (files) => {
          (async () => {
            if (!uploadImageOnRichTextPaste) return;
            const filesResult = await mediaFileReader(
              files,
              [ACCEPTABLE_IMAGE_TYPES].flatMap((x) => x),
            );
            for (const { file } of filesResult) {
              if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
                const piFile = await uploadFileFromBlob([file]);
                if (piFile) {
                  editor.update(() => {
                    const imageNode = $createImageNode(piFile.id, piFile);
                    $insertNodeToNearestRoot(imageNode);
                    if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
                      $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
                    }
                  });
                }
              }
            }
          })();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, createRecDraftNode, uploadImageOnRichTextPaste, autoGenerateRecs]);

  return <>{inputNodes}</>;
}

function extractIdFromUrl(str: string) {
  const match = str.match(/rec\/([^/]+)/);
  return match ? match[1] : null;
}

function $imageOrImageChild(node: LexicalNode) {
  return (
    $isImageNode(node) ||
    ($isLinkNode(node) && node.getChildren().find((child) => $isImageNode(child)))
  );
}

export function eventFiles(event: DragEvent | PasteCommandType): [boolean, Array<File>, boolean] {
  let dataTransfer: null | DataTransfer = null;
  if (objectKlassEquals(event, DragEvent)) {
    dataTransfer = (event as DragEvent).dataTransfer;
  } else if (objectKlassEquals(event, ClipboardEvent)) {
    dataTransfer = (event as ClipboardEvent).clipboardData;
  }

  if (dataTransfer === null) {
    return [false, [], false];
  }

  const types = dataTransfer.types;
  const hasFiles = types.includes('Files');
  const hasContent = types.includes('text/html') || types.includes('text/plain');
  return [hasFiles, Array.from(dataTransfer.files), hasContent];
}
